<template>
  <div class="max-w-115rem">
    <h6>Job details</h6>
    <div class="row">
      <div class="col-sm-6">
        <ValidationProvider
          name="job_title"
        >
          <div
            id="job-title"
            class="form-group"
          >
            <div class="row">
              <div class="col-md-12">
                <div class="label-with-tooltip">
                  <label class="control-label">Job title</label>
                  <i
                    v-if="userSCIMManaged"
                    v-tooltip
                    class="hi hi-info-filled a-tooltip"
                    data-placement="right"
                    data-original-title="Member managed by SCIM"
                  />
                </div>
                <MemberProfileSelectCustom
                  v-model="jobTitleUUID"
                  field="job_title"
                  :disabled="userSCIMManaged"
                  :options="customOptions('job_title')"
                  :select2-options="jobTitleSelect2Options"
                  @open="showSelectionsDialog"
                />
              </div>
            </div>
          </div>
        </ValidationProvider>
        <ValidationProvider
          v-slot="{ errors }"
          name="occupation_id"
        >
          <div
            id="job-type"
            class="form-group"
            :class="{ 'has-error': errors[0] }"
          >
            <label class="control-label">Job type</label>
            <AppSelect
              v-model="occupation"
              :options="occupationOptions"
              :select2-options="jobTypeSelect2Options"
            />
            <AppDialogError :errors="errors" />
          </div>
        </ValidationProvider>
      </div>
      <div class="col-sm-6">
        <ValidationProvider
          v-slot="{ errors }"
          name="department"
        >
          <div
            id="department"
            class="form-group"
            :class="{ 'has-error': errors[0] }"
          >
            <div class="label-with-tooltip">
              <label class="control-label">Department</label>
              <i
                v-if="userSCIMManaged"
                v-tooltip
                class="hi hi-info-filled a-tooltip"
                data-placement="right"
                data-original-title="Member managed by SCIM"
              />
            </div>
            <MemberProfileSelectCustom
              v-model="departmentUUID"
              field="department"
              :disabled="userSCIMManaged"
              :options="customOptions('department')"
              :select2-options="departmentSelect2Options"
              @open="showSelectionsDialog"
            />
          </div>
        </ValidationProvider>
        <ValidationProvider
          ref="workAddress"
          v-slot="{ errors }"
          name="workAddress"
        >
          <div
            id="work-address"
            class="form-group"
            :class="{ 'has-error': errors[0] }"
          >
            <div class="label-with-extra">
              <div class="label-with-tooltip">
                <label class="control-label">Work address</label>
                <i
                  v-tooltip
                  class="hi hi-info-filled a-tooltip"
                  data-placement="right"
                  data-original-title="Where work physically happens"
                />
              </div>
              <div class="text-lighter">
                <AppCheckbox
                  v-model="workMailingAddress"
                  :disabled="!workAddress"
                >
                  Mailing address
                </AppCheckbox>
              </div>
            </div>
            <GmapAutocomplete
              id="work-address-autocomplete"
              class="form-control"
              :value="workAddress"
              placeholder="Search for an address"
              @place_changed="onSetAddress('workAddress', $event)"
              @input.native="onChangeAddress('workAddress', $event)"
              @blur="onManualAddressEntry('workAddress', $event)"
            />
            <span class="help-block">{{ errors[0] }}</span>
          </div>
        </ValidationProvider>
      </div>
    </div>

    <h6>Hiring details</h6>
    <div class="row">
      <div class="col-sm-6">
        <ValidationProvider name="employment_type">
          <div
            id="employment-type"
            class="form-group"
          >
            <label class="control-label">Employment type</label>
            <AppSelect
              v-model="employmentType"
              :options="employmentTypeOptions"
              :select2-options="employmentTypeSelect2Options"
            />
          </div>
        </ValidationProvider>

        <ValidationProvider name="workplace_model">
          <div
            id="workplace-model"
            class="form-group"
          >
            <label class="control-label">In-office / Remote</label>
            <AppSelect
              v-model="workplaceModel"
              :options="workplaceModelOptions"
              :select2-options="workplaceModelSelect2Options"
              @input="updateWorkplacePercentages"
            />
          </div>
        </ValidationProvider>

        <div class="row">
          <div class="col-sm-6">
            <ValidationProvider name="in_office_percent">
              <div
                id="in-office-percent"
                class="form-group"
              >
                <label class="control-label">% In-office</label>
                <AppSelect
                  v-model="inOfficePercent"
                  :options="modelOptions"
                  :select2-options="inOfficePercentSelect2Options"
                  @input="updateWorkplacePercentage('in-office')"
                />
              </div>
            </ValidationProvider>
          </div>

          <div class="col-sm-6">
            <ValidationProvider name="remote_percent">
              <div
                id="remote-percent"
                class="form-group"
              >
                <label class="control-label">% Remote</label>
                <AppSelect
                  v-model="remotePercent"
                  :options="modelOptions"
                  :select2-options="remotePercentSelect2Options"
                  @input="updateWorkplacePercentage('remote')"
                />
              </div>
            </ValidationProvider>
          </div>
        </div>
      </div>

      <div class="col-sm-6">
        <ValidationProvider name="employed_through">
          <div
            id="employed-through"
            class="form-group"
          >
            <label class="control-label">Employed through</label>
            <AppSelect
              v-model="employedThrough"
              :options="employedThroughOptions"
              :select2-options="employedThroughSelect2Options"
            />
          </div>
        </ValidationProvider>

        <ValidationProvider name="employed_through_name">
          <div
            v-if="showEmployedThroughName"
            id="employed-through-name"
            class="form-group"
          >
            <label class="control-label">Name of {{ employedThrough }}</label>
            <AppInput
              v-model="employedThroughName"
              maxlength="255"
            />
          </div>
        </ValidationProvider>
      </div>
    </div>

    <h6>Accounting</h6>
    <div class="row">
      <div class="col-sm-6">
        <ValidationProvider name="tax_info">
          <div
            id="tax-info"
            class="form-group"
          >
            <label class="control-label">Tax info</label>
            <AppInput
              v-model="taxInfo"
              :readonly="disabled"
              placeholder="No tax info"
              maxlength="255"
            />
          </div>
        </ValidationProvider>

        <ValidationProvider
          name="tax_type"
        >
          <div
            id="tax-type"
            class="form-group"
          >
            <label class="control-label">Tax type</label>
            <MemberProfileSelectCustom
              v-model="taxTypeUUID"
              field="tax_type"
              :options="customOptions('tax_type')"
              :select2-options="taxTypeSelect2Options"
              @open="showSelectionsDialog"
            />
          </div>
        </ValidationProvider>
      </div>
      <div class="col-sm-6">
        <ValidationProvider name="account_code">
          <div
            id="account-code"
            class="form-group"
          >
            <label class="control-label">Account code</label>
            <AppInput
              v-model="accountCode"
              :readonly="disabled"
              placeholder="No account code"
              maxlength="255"
            />
          </div>
        </ValidationProvider>

        <ValidationProvider name="currency">
          <div
            id="currency"
            class="form-group"
          >
            <div class="label-with-tooltip">
              <label class="control-label">Currency</label>
              <i
                v-tooltip
                class="hi hi-info-filled a-tooltip"
                data-placement="right"
                data-original-title="This is the currency used to send money to this member when payment integrations are used"
              />
            </div>
            <AppInput
              v-model="currency"
              readonly
              placeholder="Currency"
              maxlength="255"
            />
          </div>
        </ValidationProvider>
      </div>
    </div>
    <h6>Timeline</h6>
    <div class="row">
      <div class="col-sm-6">
        <div class="form-group">
          <div class="label-with-tooltip">
            <label class="control-label">Start date</label>
            <i
              v-tooltip
              class="hi hi-info-filled a-tooltip"
              data-placement="right"
              data-original-title="First day working"
            />
          </div>
          <div class="vue-app-datetime-range-picker">
            <div class="datepicker-container">
              <ValidationProvider name="startedOn">
                <AppDatePicker
                  id="started-on"
                  v-model="startedOn"
                  type="daily"
                  :start-week-on="startWeekOn"
                  :show-quick-pick="false"
                  extra-input-classes="optional"
                />
              </ValidationProvider>
            </div>
          </div>
        </div>

        <div class="form-group">
          <div class="label-with-extra">
            <div class="label-with-tooltip">
              <label class="control-label">End date</label>
              <i
                v-tooltip
                class="hi hi-info-filled a-tooltip"
                data-placement="right"
                data-original-title="Last day working"
              />
            </div>
            <a
              v-if="canOffboard && !startOffboarding"
              class="clickable strong start-offboarding"
              @click="onStartOffboarding"
            >
              Begin offboarding
            </a>
            <a
              v-else-if="stoppedOn"
              class="clickable strong cancel-offboarding"
              @click="onCancelOffboarding"
            >
              Cancel offboarding
            </a>
          </div>

          <div class="vue-app-datetime-range-picker">
            <div class="datepicker-container">
              <ValidationProvider name="stoppedOn">
                <AppDatePicker
                  id="stopped-on"
                  v-model="stoppedOn"
                  type="daily"
                  :disabled="!offboardingInProgress"
                  :start-week-on="startWeekOn"
                  :show-quick-pick="false"
                  extra-input-classes="optional"
                />
              </ValidationProvider>
            </div>
          </div>
        </div>

        <ValidationProvider name="termination_reason">
          <div
            id="termination-reason"
            class="form-group"
          >
            <label class="control-label">Termination reason</label>
            <AppSelect
              v-model="terminationReason"
              :options="terminationReasonOptions"
              :disabled="!offboardingInProgress"
              :select2-options="terminationSelect2Options"
            />
          </div>
        </ValidationProvider>
      </div>

      <div class="col-sm-6">
        <ValidationProvider name="employment_comments">
          <div
            id="employment-comments"
            class="form-group"
          >
            <label class="control-label">Employment comments</label>
            <textarea
              v-model="employmentComment"
              class="form-control"
              maxlength="240"
              rows="3"
            />
          </div>
        </ValidationProvider>
      </div>
    </div>

    <MemberProfileManageSelectionsDialog
      v-if="showProfileManageDialog"
      :field="currentFieldEditing"
      :field-selections="currentCustomFields"
      :hidden="manageSelectionsDialogHidden"
      @close="closeSelectionsDialog"
      @open-selection="showSelectionDialog"
    />

    <MemberProfileManageSelectionDialog
      v-if="showProfileManageSelectionDialog"
      :field="currentFieldEditing"
      :selection="currentSelectionEditing"
      :field-selections="currentCustomFields"
      :hidden="manageSelectionDialogHidden"
      @close="closeSelectionDialog"
      @open-confirm="showConfirmDialog"
    />

    <MemberProfileCustomConfirmDialog
      v-if="showProfileConfirmDialog"
      :field="currentFieldEditing"
      :selection="currentSelectionEditing"
      :new-attributes="newAttributes"
      @close="closeConfirmDialog"
      @close-all="closeCustomSelectionDialogs"
    />
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { mapFormFields } from '~/utils/form_utils';
import {
  EMPLOYMENT_TYPE_OPTIONS,
  WORKPLACE_MODEL_OPTIONS,
  EMPLOYED_THROUGH_OPTIONS,
  TERMINATION_REASON_OPTIONS,
} from '~/app_constants';

import dayjs from 'dayjs';
import dayjsLocaleData from 'dayjs/plugin/localeData';
import { map, range, isNull } from 'lodash-es';
import AddressMixin from '~/mixins/address_utils';
import MemberProfileSelectCustom from '~/components/members/MemberProfileSelectCustom.vue';
import MemberProfileManageSelectionsDialog from '~/components/members/MemberProfileManageSelectionsDialog.vue';
import MemberProfileManageSelectionDialog from '~/components/members/MemberProfileManageSelectionDialog.vue';
import MemberProfileCustomConfirmDialog from '~/components/members/MemberProfileCustomConfirmDialog.vue';
dayjs.extend(dayjsLocaleData);

export default {
  name: 'MemberDialogTabEmployment',
  components: {
    MemberProfileCustomConfirmDialog,
    MemberProfileManageSelectionDialog,
    MemberProfileManageSelectionsDialog,
    MemberProfileSelectCustom,
  },
  mixins: [AddressMixin],
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    defaultSelect2Options: Object.freeze({
      allowClear: true,
      width: '100%',
    }),
    canOffboard: false,
    startOffboarding: false,
    showProfileManageDialog: false,
    showProfileManageSelectionDialog: false,
    showProfileConfirmDialog: false,
    currentFieldEditing: '',
    currentSelectionEditing: {},
    currentCustomFields: [],
    manageSelectionsDialogHidden: false,
    manageSelectionDialogHidden: false,
    newAttributes: {},
  }),
  computed: {
    ...mapState(['membership', 'organization', 'errors']),
    ...mapState({
      occupationOptions: (state) => state.occupations,
      customFields: (state) => state.custom_fields,
    }),
    ...mapState({
      user: (state) => state.membership.user,
    }),
    ...mapGetters(['startWeekOn']),
    ...mapFormFields('membership', {
      occupation: 'occupation_id',
      employeeId: 'employee_id',
      taxInfo: 'tax_info',
      jobTitle: 'job_title',
      jobTitleUUID: 'job_title_uuid',
      startedOnRaw: 'started_on',
      stoppedOnRaw: 'stopped_on',
      workAddress: 'work_address',
      workAddressState: 'work_address_state',
      workAddressCountry: 'work_address_country',
      workMailingAddress: 'work_mailing_address',
      homeMailingAddress: 'home_mailing_address',
      currency: 'currency',
      accountCode: 'account_code',
      employmentType: 'employment_type',
      workplaceModel: 'workplace_model',
      inOfficePercent: 'in_office_percent',
      remotePercent: 'remote_percent',
      employedThrough: 'employed_through',
      employedThroughName: 'employed_through_name',
      employmentComment: 'employment_comment',
      terminationReason: 'termination_reason',
      department: 'department',
      departmentUUID: 'department_uuid',
      taxType: 'tax_type',
      taxTypeUUID: 'tax_type_uuid',
    }),
    startedOn: {
      get() {
        return this.startedOnRaw ? moment(this.startedOnRaw) : null;
      },
      set(newValue) {
        this.startedOnRaw = newValue.format('YYYY-MM-DD');
      },
    },
    stoppedOn: {
      get() {
        return this.stoppedOnRaw ? moment(this.stoppedOnRaw) : null;
      },
      set(newValue) {
        this.stoppedOnRaw = newValue.format('YYYY-MM-DD');
      },
    },
    jobTitleSelect2Options() {
      return { ...this.defaultSelect2Options, placeholder: 'Select the job title' };
    },
    jobTypeSelect2Options() {
      return { ...this.defaultSelect2Options, placeholder: 'Select the job type' };
    },
    departmentSelect2Options() {
      return { ...this.defaultSelect2Options, placeholder: 'Select the department' };
    },
    employmentTypeSelect2Options() {
      return { ...this.defaultSelect2Options, placeholder: 'Select the employment type' };
    },
    employmentTypeOptions() {
      return EMPLOYMENT_TYPE_OPTIONS;
    },
    workplaceModelSelect2Options() {
      return { ...this.defaultSelect2Options, placeholder: 'Select the workplace model' };
    },
    workplaceModelOptions() {
      return WORKPLACE_MODEL_OPTIONS;
    },
    inOfficePercentSelect2Options() {
      return { ...this.defaultSelect2Options, placeholder: 'In-office percentage', allowClear: false };
    },
    remotePercentSelect2Options() {
      return { ...this.defaultSelect2Options, placeholder: 'Remote percentage', allowClear: false };
    },
    employedThroughSelect2Options() {
      return { ...this.defaultSelect2Options, placeholder: 'Select the hiring arrangement' };
    },
    employedThroughOptions() {
      return EMPLOYED_THROUGH_OPTIONS;
    },
    showEmployedThroughName() {
      return this.employedThrough && this.employedThrough !== 'direct';
    },
    modelOptions() {
      return map(range(100, -1), (num) => (num === 0 ? { id: 0, text: '-' } : { id: num, text: `${num}%` }));
    },
    taxTypeSelect2Options() {
      return { ...this.defaultSelect2Options, placeholder: 'Select the tax type' };
    },
    terminationSelect2Options() {
      return { ...this.defaultSelect2Options, placeholder: 'Select the termination reason' };
    },
    terminationReasonOptions() {
      return TERMINATION_REASON_OPTIONS;
    },
    offboardingInProgress() {
      return this.startOffboarding;
    },
    userSCIMManaged() {
      return this.user.scim_managed;
    },
  },
  watch: {
    workMailingAddress(newVal) {
      if (newVal) {
        this.homeMailingAddress = false;
      }
    },
    workAddress(newVal) {
      if (!newVal) {
        this.workMailingAddress = false;
      }
    },
    customFields(newVal) {
      if (this.currentFieldEditing) {
        this.currentCustomFields = newVal[this.currentFieldEditing]?.options;
      }
    },
  },
  created() {
    this.loadCustomFields();
  },
  mounted() {
    this.canOffboard = isNull(this.stoppedOn);
  },
  methods: {
    ...mapActions(['loadCustomFields']),
    onStartOffboarding() {
      this.startOffboarding = true;
    },
    customOptions(type) {
      return this.customFields[type]?.options?.map((obj) => ({ id: obj.id, text: obj.title }));
    },
    onCancelOffboarding() {
      this.stoppedOnRaw = null;
      this.terminationReason = null;
      this.canOffboard = true;
    },
    updateWorkplacePercentages() {
      if (this.workplaceModel === 'in_office') {
        this.inOfficePercent = 100;
        this.remotePercent = 0;
      } else if (this.workplaceModel === 'remote') {
        this.inOfficePercent = 0;
        this.remotePercent = 100;
      } else if (this.workplaceModel === 'hybrid') {
        this.inOfficePercent = 50;
        this.remotePercent = 50;
      } else {
        this.inOfficePercent = null;
        this.remotePercent = null;
      }
    },
    updateWorkplacePercentage(type) {
      if (type === 'in-office') {
        this.remotePercent = 100 - this.inOfficePercent;
      } else {
        this.inOfficePercent = 100 - this.remotePercent;
      }

      if (Number(this.inOfficePercent) === 100) {
        this.workplaceModel = 'in_office';
      } else if (Number(this.remotePercent) === 100) {
        this.workplaceModel = 'remote';
      } else {
        this.workplaceModel = 'hybrid';
      }
    },
    clearStartedOn() {
      this.startedOnRaw = null;
    },
    closeSelectionsDialog() {
      this.showProfileManageDialog = false;
      this.currentFieldEditing = '';
      this.currentCustomFields = [];
    },
    showSelectionsDialog(field) {
      this.showProfileManageDialog = true;
      this.currentFieldEditing = field;
      this.currentCustomFields = this.customFields[field]?.options;
    },
    closeSelectionDialog() {
      this.showProfileManageSelectionDialog = false;
      this.currentSelectionEditing = {};
      this.manageSelectionsDialogHidden = false;
    },
    showSelectionDialog(selection) {
      this.manageSelectionsDialogHidden = true;
      this.showProfileManageSelectionDialog = true;
      this.currentSelectionEditing = selection;
    },
    showConfirmDialog(newAttributes) {
      this.manageSelectionDialogHidden = true;
      this.newAttributes = newAttributes;
      this.showProfileConfirmDialog = true;
    },
    closeConfirmDialog() {
      this.newAttributes = {};
      this.showProfileConfirmDialog = false;
      this.manageSelectionDialogHidden = false;
    },
    closeCustomSelectionDialogs() {
      this.closeConfirmDialog();
      this.closeSelectionDialog();
    },
  },
};
</script>
